import { TreeInstance } from '@headless-tree/core';
import React from 'react';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';

import { SubMenu } from './types';

type UseTaskTreeShortcutsProps = {
  tree: TreeInstance<InternalTreeItem>;
  tasks: Task[];
  open: (
    position: { left: number; top: number },
    payload: { tasks: Task[]; initialActiveSubMenu?: SubMenu },
  ) => void;
  isContextMenuOpen: boolean;
};

export const useTaskTreeShortcuts = ({
  tree,
  tasks,
  open,
  isContextMenuOpen,
}: UseTaskTreeShortcutsProps) => {
  const handleShortcut = React.useCallback(
    (subMenu?: SubMenu) => {
      if (
        isContextMenuOpen ||
        document.querySelector(
          `[data-interaction-for-type="${InteractionType.Task}"]`,
        ) ||
        !tree
      ) {
        return;
      }

      const selectedItems = tree.getSelectedItems();
      const focusedItemId = tree.getState().focusedItem;

      if (!selectedItems.length && !focusedItemId) {
        return;
      }

      // Determine valid items (Selection only)
      const targetItemIds = selectedItems.map((i) => i.getId());

      // Map to Task objects
      // Note: We need to look up using sourceId
      // We need to find the items from the tree first to get sourceId
      const targetTasks = targetItemIds
        .map((id) => {
          const item = tree.getItems().find((i) => i.getId() === id);
          return item
            ? tasks.find((t) => t.id === item.getItemData().sourceId)
            : undefined;
        })
        .filter(Boolean) as Task[];

      if (!targetTasks.length) {
        return;
      }

      // Find Anchor Element
      // We prioritize the focused item if it exists in the target list (for intuitive positioning)
      let anchorElement: Element | null = null;

      if (focusedItemId && targetItemIds.includes(focusedItemId)) {
        anchorElement = document.querySelector(
          `[data-item-id="${focusedItemId}"]`,
        );
      }

      // Otherwise, we look for ANY of the target items that are currently mounted/visible
      if (!anchorElement) {
        for (const id of targetItemIds) {
          const el = document.querySelector(`[data-item-id="${id}"]`);
          if (el) {
            anchorElement = el;
            break;
          }
        }
      }

      if (!anchorElement) {
        // If no items are visible, we cannot show the menu anchored to them.
        return;
      }

      const containerRect = anchorElement.getBoundingClientRect();

      open(
        {
          top: containerRect.top + containerRect.height / 2,
          left: containerRect.left + containerRect.width / 2,
        },
        {
          tasks: targetTasks,
          initialActiveSubMenu: subMenu,
        },
      );
    },
    [tree, tasks, isContextMenuOpen, open],
  );

  useKeysDown(
    ['KeyP'],
    (e) => {
      e.preventDefault();
      handleShortcut(SubMenu.Priority);
    },
    { ignoreInputFields: true },
  );

  useKeysDown(
    ['KeyR'],
    (e) => {
      e.preventDefault();
      handleShortcut(SubMenu.ReminderTime);
    },
    { ignoreInputFields: true },
  );

  useKeysDown(
    ['KeyE'],
    (e) => {
      e.preventDefault();
      handleShortcut();
    },
    { ignoreInputFields: true },
  );

  useKeysDown(
    ['KeyG'],
    (e) => {
      e.preventDefault();
      handleShortcut(SubMenu.LinkedItem);
    },
    { ignoreInputFields: true },
  );

  useKeysDown(
    ['KeyD'],
    (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        handleShortcut(SubMenu.DeadlineDeadline);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      e.preventDefault();
      handleShortcut(SubMenu.Date);
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyT'],
    (e) => {
      if (e.shiftKey) {
        return;
      }
      e.preventDefault();
      handleShortcut(SubMenu.Tags);
    },
    { ignoreInputFields: true },
  );
};
